/**
 * Project: Aniatek
 * Author: Carlos A. Loaiza | Connect & Launch
 * Tel: +52 (667) 247 - 6316
 * Intellectual Property: All rights reserved (c) 2026
 */

<template>
    <LayoutPrincipal>
        <ContainerWhite>
            <div class="membership-wall-container container-relative">

                <div v-if="loading" class="loader-overlay-confined">
                    <div class="loader-content">
                        <LoadScreen />
                        <p class="loader-text">Verificando confirmación de Stripe...</p>
                    </div>
                </div>

                <div :class="{ 'membership-card': true, 'hidden-content': loading }">

                    <div v-if="esExitoso">
                        <div class="icon-section">
                            <i class="fa-solid fa-circle-check confirmation-icon"></i>
                        </div>

                        <h1 class="wall-title success-title">¡Pago Exitoso!</h1>

                        <p class="wall-subtitle">
                            Hemos recibido tu pago correctamente. Tu membresía ha sido activada.
                            Ahora puedes acceder a la plataforma completa y disfrutar de todos los beneficios exclusivos.
                        </p>

                        <div class="benefit-list success-list">
                            <h3>Estado de tu Membresía:</h3>
                            <ul>
                                <li><i class="fa-solid fa-star check-icon"></i> Plan: Membresía Instalador ($999/mes)
                                </li>
                                <li><i class="fa-solid fa-clock check-icon"></i> Estatus: <strong>ACTIVO</strong></li>
                                <li><i class="fa-solid fa-receipt check-icon"></i> Ref: {{ displaySessionId }}</li>
                            </ul>
                        </div>

                        <button @click="irAlDashboard" class="cta-button access-button">
                            Acceder al Dashboard
                        </button>
                    </div>

                    <div v-else>
                        <div class="icon-section">
                            <i class="fa-solid fa-hourglass-half confirmation-icon warning-icon"></i>
                        </div>

                        <h1 class="wall-title warning-title">Procesando Pago...</h1>

                        <p class="wall-subtitle">
                            Stripe ha recibido tu solicitud, pero aún estamos esperando la confirmación final en nuestro sistema.
                            Esto es normal y suele tomar unos segundos.
                        </p>

                        <div class="benefit-list warning-list">
                            <h3>¿Qué debo hacer?</h3>
                            <ul>
                                <li>No cierres esta ventana.</li>
                                <li>Presiona el botón de abajo para verificar nuevamente.</li>
                            </ul>
                        </div>

                        <button @click="verificarEstado" class="cta-button retry-button">
                            Verificar Nuevamente
                        </button>

                        <p class="wall-footer">
                            Si el problema persiste, contacta a soporte con la referencia: {{ displaySessionId }}
                        </p>
                    </div>

                    <p v-if="esExitoso" class="wall-footer">
                        Gracias por confiar en nosotros.
                    </p>
                </div>
            </div>
        </ContainerWhite>
    </LayoutPrincipal>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import LayoutPrincipal from '@/layouts/layoutPrincipal.vue';
import ContainerWhite from '@/layouts/ContainerWhite.vue';
import LoadScreen from '../components/Forms/LoadScreen.vue';
import pagosService from '@/services/pagosService';
import userService from '@/services/userService.js';

const router = useRouter();
const route = useRoute();
const loading = ref(true);
const esExitoso = ref(false);

const sessionId = computed(() => route.query.session_id || 'Pendiente');

const displaySessionId = computed(() => {
    const id = sessionId.value;
    if (id && id.length > 10) {
        return id.substring(0, 4) + '...' + id.substring(id.length - 6);
    }
    return id;
});

const verificarEstado = async () => {
    loading.value = true;
    esExitoso.value = false;

    try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const responsePago = await pagosService.verificarEstatusSuscripcion();
    
        const esValido = ['active', 'trialing'].includes(responsePago.data.status);

        if (esValido) {
            const localString = localStorage.getItem('userData');
            if (localString) {
                const localData = JSON.parse(localString);
                const userId = localData.id_usuario;

                console.log("🔄 Sincronizando datos tras pago exitoso...");
                const userResponse = await userService.getUserByID(userId);
                
                if (userResponse.data) {
                    localStorage.setItem('userData', JSON.stringify(userResponse.data));
                    console.log("✅ Sistema sincronizado. El usuario ya no está bloqueado.");
                }
            }

            esExitoso.value = true;
        } else {
            esExitoso.value = false;
        }

    } catch (error) {
        console.error("Error en la confirmación:", error);
        esExitoso.value = false;
    } finally {
        loading.value = false;
    }
};

const irAlDashboard = () => {
    router.push('/Dashboard');
};

onMounted(() => {
    verificarEstado();
});

</script>

<style scoped>
.membership-wall-container {
    --primary-color: #29b9f6;
    --success-color: #28a745;
    --warning-color: #ffc107;
    --warning-text: #856404;
    --text-color: #333;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 76vh;
    position: relative;
}

.loader-overlay-confined {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.98);
    z-index: 10;
}

.loader-text {
    margin-top: 15px;
    font-weight: 600;
    color: #666;
}

.hidden-content {
    visibility: hidden;
    opacity: 0;
}

.membership-card {
    background: #ffffff;
    padding: 40px;
    border-radius: 15px;
    text-align: center;
    max-width: 550px;
    width: 100%;
    transition: opacity 0.3s ease;
    z-index: 5;
}

.confirmation-icon {
    font-size: 50px;
    color: var(--success-color);
    background-color: rgba(40, 167, 69, 0.1);
    padding: 20px;
    border-radius: 50%;
}

.warning-icon {
    color: var(--warning-color);
    background-color: rgba(255, 193, 7, 0.1);
}

.success-title {
    color: var(--success-color);
    font-weight: 700;
}

.warning-title {
    color: #d39e00;
    font-weight: 700;
}

.success-list {
    border-left: 4px solid var(--success-color);
}

.warning-list {
    border-left: 4px solid var(--warning-color);
}

.access-button {
    background-color: var(--success-color);
}

.access-button:hover {
    background-color: #218838;
    transform: translateY(-2px);
}

.retry-button {
    background-color: #6c757d;
}

.retry-button:hover {
    background-color: #5a6268;
}

.icon-section {
    margin-bottom: 25px;
}

.wall-title {
    font-size: 30px;
    margin-bottom: 15px;
}

.wall-subtitle {
    font-size: 18px;
    color: var(--text-color);
    margin-bottom: 30px;
    line-height: 1.5;
    text-align: justify;
}

.benefit-list {
    text-align: left;
    margin: 30px auto;
    padding: 0 20px;
}

.benefit-list h3 {
    color: #555;
    margin-bottom: 10px;
    font-weight: bold;
}

.benefit-list ul {
    list-style: none;
    padding: 0;
}

.benefit-list li {
    margin-bottom: 8px;
    font-size: 16px;
    color: #555;
}

.check-icon {
    color: var(--success-color);
    margin-right: 8px;
}

.cta-button {
    border: none;
    padding: 15px 30px;
    font-size: 20px;
    font-weight: bold;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.3s, transform 0.1s;
    width: 100%;
    margin-top: 20px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    color: white;
}

.wall-footer {
    margin-top: 20px;
    font-size: 14px;
    color: #999;
}

@media (max-width: 600px) {
    .membership-card {
        padding: 25px 20px;
        margin: 20px;
    }

    .wall-title {
        font-size: 24px;
    }

    .wall-subtitle {
        font-size: 16px;
    }

    .cta-button {
        font-size: 18px;
    }
}
</style>